import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelySolLewittMode({ brand }: ModeRendererProps) {
  // Rule-based aesthetics: algorithms turned into walls of logic
  // Instructional patterns, serial systems, axioms
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Rule 1: Horizontal lines from left to right */}
      {Array.from({ length: 12 }).map((_, i) => {
        const y = 80 + i * 25;
        return (
          <line
            key={`rule1-${i}`}
            x1="80"
            y1={y}
            x2="320"
            y2={y}
            stroke={brand.deep}
            strokeWidth="2"
            opacity={0.7}
          />
        );
      })}
      
      {/* Rule 2: Vertical lines from top to bottom */}
      {Array.from({ length: 12 }).map((_, i) => {
        const x = 360 + i * 25;
        return (
          <line
            key={`rule2-${i}`}
            x1={x}
            y1="80"
            x2={x}
            y2="380"
            stroke={brand.violet}
            strokeWidth="2"
            opacity={0.7}
          />
        );
      })}
      
      {/* Rule 3: Diagonal lines (45°) */}
      {Array.from({ length: 15 }).map((_, i) => {
        const offset = i * 20;
        return (
          <line
            key={`rule3-${i}`}
            x1={80 + offset}
            y1="440"
            x2={80 + offset + 150}
            y2="590"
            stroke={brand.aqua}
            strokeWidth="2"
            opacity={0.6}
          />
        );
      })}
      
      {/* Rule 4: Diagonal lines (-45°) */}
      {Array.from({ length: 15 }).map((_, i) => {
        const offset = i * 20;
        return (
          <line
            key={`rule4-${i}`}
            x1={400 + offset}
            y1="440"
            x2={400 + offset + 150}
            y2="590"
            stroke={brand.amber}
            strokeWidth="2"
            opacity={0.6}
          />
        );
      })}
      
      {/* Serial system: Grid with systematic variation */}
      {Array.from({ length: 6 }).map((_, row) =>
        Array.from({ length: 8 }).map((_, col) => {
          const x = 80 + col * 80;
          const y = 640 + row * 70;
          const rule = (row * 8 + col) % 4;
          
          if (rule === 0) {
            // Horizontal only
            return (
              <g key={`serial-${row}-${col}`}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <line
                    key={`h-${i}`}
                    x1={x + 5}
                    y1={y + 5 + i * 7}
                    x2={x + 65}
                    y2={y + 5 + i * 7}
                    stroke={brand.deep}
                    strokeWidth="1"
                    opacity={0.6}
                  />
                ))}
              </g>
            );
          } else if (rule === 1) {
            // Vertical only
            return (
              <g key={`serial-${row}-${col}`}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <line
                    key={`v-${i}`}
                    x1={x + 5 + i * 7}
                    y1={y + 5}
                    x2={x + 5 + i * 7}
                    y2={y + 60}
                    stroke={brand.violet}
                    strokeWidth="1"
                    opacity={0.6}
                  />
                ))}
              </g>
            );
          } else if (rule === 2) {
            // Diagonal /
            return (
              <g key={`serial-${row}-${col}`}>
                {Array.from({ length: 10 }).map((_, i) => (
                  <line
                    key={`d1-${i}`}
                    x1={x + i * 7}
                    y1={y + 5}
                    x2={x + 65}
                    y2={y + 60 - i * 7}
                    stroke={brand.aqua}
                    strokeWidth="1"
                    opacity={0.5}
                  />
                ))}
              </g>
            );
          } else {
            // Diagonal \
            return (
              <g key={`serial-${row}-${col}`}>
                {Array.from({ length: 10 }).map((_, i) => (
                  <line
                    key={`d2-${i}`}
                    x1={x + 5}
                    y1={y + 5 + i * 6}
                    x2={x + 65 - i * 6}
                    y2={y + 60}
                    stroke={brand.amber}
                    strokeWidth="1"
                    opacity={0.5}
                  />
                ))}
              </g>
            );
          }
        })
      )}
      
      {/* Vasarely optical overlay - systematic distortion */}
      {Array.from({ length: 10 }).map((_, i) => {
        const size = 200 - i * 18;
        const offset = i * 9;
        return (
          <rect
            key={`optical-${i}`}
            x={170 + offset}
            y={180 + offset}
            width={size}
            height={size}
            fill="none"
            stroke={i % 2 === 0 ? brand.violet : brand.aqua}
            strokeWidth="1"
            opacity={0.3 - i * 0.02}
          />
        );
      })}
      
      {/* Axiom markers */}
      <text x="85" y="70" fontFamily="monospace" fontSize="10" fill={brand.deep} opacity={0.5}>
        Rule 1: H Lines
      </text>
      <text x="365" y="70" fontFamily="monospace" fontSize="10" fill={brand.violet} opacity={0.5}>
        Rule 2: V Lines
      </text>
      <text x="85" y="430" fontFamily="monospace" fontSize="10" fill={brand.aqua} opacity={0.5}>
        Rule 3: / Lines
      </text>
      <text x="405" y="430" fontFamily="monospace" fontSize="10" fill={brand.amber} opacity={0.5}>
        Rule 4: \ Lines
      </text>
    </g>
  );
}
